/**
 * Mémorise la page demandée avant une redirection vers la connexion, pour y
 * revenir une fois l'utilisateur authentifié.
 *
 * Le cas qui motive ce module : les notifications par email pointent vers
 * `/payslips`. Un salarié qui clique depuis son téléphone n'a le plus souvent
 * pas de session active — sans mémorisation il atterrit sur l'accueil et doit
 * retrouver seul ses fiches de paie, alors que c'est précisément ce qu'on
 * l'invitait à consulter.
 *
 * `sessionStorage` et non `localStorage` : l'intention est celle d'une
 * navigation en cours, elle ne doit pas survivre à la fermeture de l'onglet.
 */

const KEY = 'redirectAfterLogin'

// Étapes du parcours de connexion : y « revenir » n'aurait aucun sens.
const NON_DESTINATIONS = ['/login', '/select-establishment']

/**
 * N'accepte qu'un chemin interne.
 *
 * `//exemple.com` est une URL dite protocol-relative, que le navigateur suit
 * vers l'extérieur : une valeur lue depuis le stockage ne doit jamais pouvoir
 * se transformer en redirection ouverte.
 */
function isInternalPath(value) {
  return typeof value === 'string' && value.startsWith('/') && !value.startsWith('//')
}

export function rememberRedirect(fullPath) {
  if (!isInternalPath(fullPath)) return
  if (NON_DESTINATIONS.some((path) => fullPath === path || fullPath.startsWith(`${path}?`))) return

  try {
    sessionStorage.setItem(KEY, fullPath)
  } catch {
    // Navigation privée ou stockage refusé : on se rabattra sur l'accueil.
  }
}

export function peekRedirect() {
  try {
    const value = sessionStorage.getItem(KEY)
    return isInternalPath(value) ? value : null
  } catch {
    return null
  }
}

/** Lit la destination et l'oublie : elle ne doit servir qu'une fois. */
export function consumeRedirect() {
  const value = peekRedirect()
  clearRedirect()
  return value
}

export function clearRedirect() {
  try {
    sessionStorage.removeItem(KEY)
  } catch {
    // Rien à nettoyer si le stockage est indisponible.
  }
}
